// @ts-check
// zoom-to-labyrinth.ts — the ONLY entry to the Labyrinth.
//
// There is no Labyrinth button: you reach the map by ZOOMING OUT of the note
// you're in. A zoom-out gesture on the editor hands the current note to the
// Labyrinth (setPendingLabyrinthFocus) and opens it, which makes the map's
// camera start framed on that note's node and pull back to the whole vault — the
// animated note→map zoom (the reverse, clicking a node, zooms back into a note;
// see labyrinth-view.ts). Triggers, all meaning "zoom out":
//   • Desktop: ⌘/Ctrl + wheel in the zoom-out direction (trackpad pinch-in,
//     ⌘+scroll-down, or mouse-wheel zoom) — preventDefault beats the page zoom.
//   • Touch: two-finger pinch-IN (fingers together).
//   • Keyboard (accessible equivalent): ⌘/Ctrl+Shift+L.
// Scoped to #cm-editor, which is display:none while the map is shown, so this
// never fights the Labyrinth canvas's own pinch-zoom.

import { getCurrentFileName, getReadOnlyViewActive, LABYRINTH_NOTE } from '../core/app-state.js';
import { setPendingLabyrinthFocus, setZoomTransition } from '../notes/labyrinth-state.js';
import { loadNote } from '../notes/note-actions.js';

// A pinch / wheel gesture fires MANY events; this guard makes the open fire once
// per gesture, so we never kick off two overlapping loadNote('Labyrinth') opens
// (a double-mount race that left the camera parked on the node-fill close-up
// instead of pulling back to the whole map).
let _entering = false;
function enterLabyrinth(): void {
  if (_entering) return;
  const from = getCurrentFileName();
  // Only zoom out FROM a real note; never re-enter while the map is already up.
  if (!from || from === LABYRINTH_NOTE || getReadOnlyViewActive()) return;
  _entering = true;
  setPendingLabyrinthFocus(from); // camera starts framed on this note's node
  setZoomTransition(true); // editor↔canvas crossfade (not a hard cut)
  loadNote(LABYRINTH_NOTE);
  window.setTimeout(() => {
    _entering = false;
  }, 1200);
}

// Own the browser's page-zoom gestures app-wide so a pinch / ⌘-scroll zooms the
// NOTE or the LABYRINTH (which have their own zoom), never the page itself —
// otherwise the page and the content fight and zooming feels broken. (The mobile
// viewport meta already sets user-scalable=no; this covers desktop trackpad
// pinch, the WebKit gesture events, and the keyboard zoom shortcuts.)
function preventPageZoom(): void {
  window.addEventListener(
    'wheel',
    (e) => {
      if ((e as WheelEvent).ctrlKey) e.preventDefault(); // trackpad pinch / ctrl+wheel page zoom
    },
    { passive: false }
  );
  for (const ev of ['gesturestart', 'gesturechange', 'gestureend']) {
    document.addEventListener(ev, (e) => e.preventDefault(), { passive: false });
  }
  window.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && ['+', '-', '=', '_', '0'].includes(e.key)) e.preventDefault();
  });
}

function touchDist(t: TouchList): number {
  return Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
}

export function setupZoomToLabyrinth(): void {
  preventPageZoom();
  const editor = document.getElementById('cm-editor');
  if (!editor) return;

  // Desktop: ⌘/Ctrl + wheel, zoom-out direction. Accumulate so a deliberate
  // pinch — not one stray notch — triggers; preventDefault stops browser zoom.
  let wheelAccum = 0;
  let wheelReset: ReturnType<typeof setTimeout> | null = null;
  editor.addEventListener(
    'wheel',
    (ev: Event) => {
      const e = ev as WheelEvent;
      if (!(e.ctrlKey || e.metaKey)) return; // a zoom gesture, not plain scroll
      e.preventDefault();
      if (e.deltaY > 0) {
        wheelAccum += e.deltaY;
        if (wheelAccum > 80) {
          wheelAccum = 0;
          enterLabyrinth();
        }
      } else {
        wheelAccum = 0; // zooming the other way cancels
      }
      if (wheelReset) clearTimeout(wheelReset);
      wheelReset = setTimeout(() => {
        wheelAccum = 0;
      }, 250);
    },
    { passive: false }
  );

  // Touch: two-finger pinch-IN (fingers together) = zoom out.
  let startDist = 0;
  editor.addEventListener(
    'touchstart',
    (ev: Event) => {
      const e = ev as TouchEvent;
      if (e.touches.length === 2) startDist = touchDist(e.touches);
    },
    { passive: true }
  );
  editor.addEventListener(
    'touchmove',
    (ev: Event) => {
      const e = ev as TouchEvent;
      if (e.touches.length !== 2 || !startDist) return;
      if (touchDist(e.touches) < startDist * 0.6) {
        e.preventDefault();
        startDist = 0;
        enterLabyrinth();
      }
    },
    { passive: false }
  );
  const clearStart = (): void => {
    startDist = 0;
  };
  editor.addEventListener('touchend', clearStart);
  editor.addEventListener('touchcancel', clearStart);

  // Accessible keyboard equivalent of the zoom gesture.
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (!(e.metaKey || e.ctrlKey) || !e.shiftKey) return;
    if (e.key !== 'l' && e.key !== 'L') return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
    e.preventDefault();
    enterLabyrinth();
  });
}
